import axiosInstance from "@/services/axios.config";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "react-toastify";

export type PublicRegistryCsvExport =
	| { resource: "projects"; projectStatus?: "IN_VALIDATION" }
	| { resource: "credits"; activity?: "TRANSFER" | "RETIREMENT" };

const DOWNLOAD_ERROR = "Could not download this registry file. Try again.";

function fallbackFilename(exportRequest: PublicRegistryCsvExport) {
	const date = new Date().toISOString().slice(0, 10);

	if (exportRequest.resource === "projects") {
		return exportRequest.projectStatus === "IN_VALIDATION"
			? `projects-in-validation-${date}.csv`
			: `registered-projects-${date}.csv`;
	}

	if (exportRequest.activity === "TRANSFER") return `credit-transfers-${date}.csv`;
	if (exportRequest.activity === "RETIREMENT") return `credit-retirements-${date}.csv`;
	return `credit-issuances-${date}.csv`;
}

function filenameFromContentDisposition(header: unknown, fallback: string) {
	if (typeof header !== "string") return fallback;
	const match = /filename="?([^";]+)"?/i.exec(header);
	return match?.[1]?.trim() || fallback;
}

async function messageFromExportError(error: unknown) {
	if (!axios.isAxiosError(error)) return DOWNLOAD_ERROR;

	const data = error.response?.data;
	try {
		if (data instanceof Blob) {
			const parsed = JSON.parse(await data.text()) as { msg?: string };
			return parsed.msg || DOWNLOAD_ERROR;
		}
		if (data && typeof data === "object" && "msg" in data && typeof data.msg === "string" && data.msg) {
			return data.msg;
		}
	} catch {
		return DOWNLOAD_ERROR;
	}

	return DOWNLOAD_ERROR;
}

function saveBlob(blob: Blob, filename: string) {
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	link.remove();
	window.URL.revokeObjectURL(url);
}

export async function downloadPublicRegistryCsv(exportRequest: PublicRegistryCsvExport) {
	const path = exportRequest.resource === "projects" ? "project/public/export" : "credit/public/export";
	const params: Record<string, string> = { format: "csv" };

	if (exportRequest.resource === "projects" && exportRequest.projectStatus) {
		params.projectStatus = exportRequest.projectStatus;
	}
	if (exportRequest.resource === "credits" && exportRequest.activity) {
		params.activity = exportRequest.activity;
	}

	const response = await axiosInstance.get<Blob>(path, {
		params,
		responseType: "blob",
		headers: { Accept: "text/csv" },
	});

	const filename = filenameFromContentDisposition(
		response.headers["content-disposition"],
		fallbackFilename(exportRequest),
	);
	const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: "text/csv" });
	saveBlob(blob, filename);
}

export function useDownloadPublicRegistryCsv() {
	return useMutation({
		mutationFn: downloadPublicRegistryCsv,
		onError: async (error) => {
			toast.error(await messageFromExportError(error));
		},
	});
}
